import { createContext, useState } from 'react';
import Cookies from 'js-cookie';
import { useRouter, NextRouter } from 'next/router';
import { IRestaurantData } from 'pages/reservation-result';
import { getUnits } from 'hooks/use-units';
import {
  GET_IN_LATITUDE_COOKIE_KEY,
  GET_IN_LONGITUDE_COOKIE_KEY,
} from 'utils/constants';
import { Event } from 'utils/helpers';
import { useAuth } from 'hooks/use-auth';

export type IFilterParams =
  | 'price_range'
  | 'occasion'
  | 'distance'
  | 'service'
  | 'cuisine_id'
  | 'button_change';

type IfiltersParamsObject = {
  [key in IFilterParams]: string;
};

type IEventName = { [key: string]: string };

interface IReservationsResultsContext {
  currentModalOpen: IFilterParams | null;
  priceRangeSelected: string;
  isModalOpen: boolean;
  isEmptyData: boolean;
  isSendingRequest: boolean;
  setIsSendingRequest: (value: boolean) => void;
  distance: number;
  restaurantsFiltered: IRestaurantData[];
  filtersParams: IfiltersParamsObject;
  setRestaurantsFiltered: (restaurants: IRestaurantData[]) => void;
  handleChangeFilters: (serviceName: IFilterParams, value: string) => void;
  checkboxIsActive: (serviceName: IFilterParams, value: string) => boolean;
  handleOpenModal: (dropdownName: IFilterParams) => void;
  handleChangeDistance: (distance: number) => void;
  handleClearFilters: (serviceName: IFilterParams) => void;
  handleCloseModal: () => void;
  handleApplyFilters: () => void;
  router: NextRouter;
}

export const ReservationsResultsContext = createContext(
  {} as IReservationsResultsContext
);

export const ReservationsResultsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [currentModalOpen, setCurrentModalOpen] =
    useState<IFilterParams | null>(null);

  const [distance, setDistance] = useState<number>(10);
  const [priceRangeSelected, setPriceRangeSelected] = useState('');
  const [typeOfServiceSelected, setTypeOfServiceSelected] = useState('');
  const [categorySelected, setCategorySelected] = useState('');
  const [occasionSelected, setOccasionSelected] = useState('');

  const [restaurantsFiltered, setRestaurantsFiltered] = useState<
    IRestaurantData[]
  >([]);
  const [filtersParams, setFiltersParams] = useState<IfiltersParamsObject>(
    {} as IfiltersParamsObject
  );

  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isEmptyData, setIsEmptyData] = useState<boolean>(false);

  const router = useRouter();

  const checkboxIsActive = (serviceName: IFilterParams, value: string) =>
    (serviceName === 'cuisine_id' && categorySelected === value) ||
    (serviceName === 'service' && typeOfServiceSelected === value) ||
    (serviceName === 'occasion' && occasionSelected === value);

  const handleClearParams = (serviceName: IFilterParams) => {
    serviceName === 'cuisine_id' && setCategorySelected('');
    serviceName === 'service' && setTypeOfServiceSelected('');
    serviceName === 'occasion' && setOccasionSelected('');
    serviceName === 'price_range' && setPriceRangeSelected('');
  };

  const handleUpdateParams = (serviceName: IFilterParams, value: string) => {
    serviceName === 'cuisine_id' && setCategorySelected(value);
    serviceName === 'service' && setTypeOfServiceSelected(value);
    serviceName === 'occasion' && setOccasionSelected(value);
    serviceName === 'price_range' && setPriceRangeSelected(value);
  };

  const handleSendGtagEvent = (serviceName: IFilterParams, value: string) => {
    const eventName: IEventName = {
      price_range: 'getin_reservation_result_price_range_click',
      occasion: 'getin_reservation_result_occasion_click',
      service: 'getin_reservation_result_service_click',
      cuisine_id: 'getin_reservation_result_cuisine_click',
    };

    Event.push(`${eventName[serviceName]}`, {
      diner_id: user?.id,
      serviceName: value,
    });
  };

  const handleChangeFilters = (serviceName: IFilterParams, value: string) => {
    if (checkboxIsActive(serviceName, value)) {
      handleClearParams(serviceName);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [serviceName]: currentValue, ...restWithoutTypeExcluded } =
        filtersParams;
      setFiltersParams(restWithoutTypeExcluded as IfiltersParamsObject);
      return;
    }

    handleUpdateParams(serviceName, value);
    handleSendGtagEvent(serviceName, value);

    setFiltersParams((oldValues) => ({
      ...oldValues,
      [serviceName]: value,
    }));
  };

  const handleOpenModal = (dropdownName: IFilterParams) => {
    setCurrentModalOpen(dropdownName);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleApplyFilters = async () => {
    setIsSendingRequest(true);

    const { data } = await getUnits({
      ...(priceRangeSelected && { price_range: priceRangeSelected }),
      ...filtersParams,
      ...router.query,
      lng: Cookies.get(GET_IN_LONGITUDE_COOKIE_KEY),
      lat: Cookies.get(GET_IN_LATITUDE_COOKIE_KEY),
      distance,
    });

    setIsEmptyData(data.length === 0);
    setRestaurantsFiltered(data);
    setIsModalOpen(false);
    setIsSendingRequest(false);
  };

  const handleClearFilters = async (serviceName: IFilterParams) => {
    serviceName === 'distance' && setDistance(10);
    serviceName === 'price_range' && setPriceRangeSelected('');

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [serviceName]: currentValue, ...restWithoutTypeExcluded } =
      filtersParams;
    setFiltersParams(restWithoutTypeExcluded as IfiltersParamsObject);

    const { data } = await getUnits({
      ...router.query,
      ...restWithoutTypeExcluded,
      lng: Cookies.get(GET_IN_LONGITUDE_COOKIE_KEY),
      lat: Cookies.get(GET_IN_LATITUDE_COOKIE_KEY),
      distance,
      page: 1,
      per_page: 6,
    });

    setIsEmptyData(data.length === 0);
    handleClearParams(serviceName);
    setRestaurantsFiltered(data);
    setIsModalOpen(false);
  };

  const handleChangeDistance = (distance: number) => {
    setDistance(distance);

    Event.push('getin_reservation_result_distance_click', {
      diner_id: user?.id,
      distance,
    });
  };

  return (
    <ReservationsResultsContext.Provider
      value={{
        handleOpenModal,
        currentModalOpen,
        isModalOpen,
        handleChangeFilters,
        checkboxIsActive,
        handleApplyFilters,
        handleCloseModal,
        handleClearFilters,
        distance,
        handleChangeDistance,
        priceRangeSelected,
        restaurantsFiltered,
        setRestaurantsFiltered,
        isEmptyData,
        filtersParams,
        isSendingRequest,
        setIsSendingRequest,
        router,
      }}
    >
      {children}
    </ReservationsResultsContext.Provider>
  );
};
